import React from 'react';
import { cn } from '@dashboard/lib/utils';
import { Badge } from '@dashboard/components/ui/badge';
import Tooltip from '@dashboard/components/Tooltip';
import * as RadixTooltip from '@radix-ui/react-tooltip';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@dashboard/components/ui/accordion';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@dashboard/components/ui/dropdown-menu';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { StarIcon } from '@heroicons/react/24/solid';
import Button from '@dashboard/components/Button';
import Link from '@dashboard/components/Link';
import FormSubmit from '@dashboard/components/Formik/components/FormSubmit';
import { useFormikContext } from 'formik';
import { usePriceEdit } from './PriceEditContext';
import PriceForm from './PriceForm';
import {
  isPriceDraftDirty,
  shouldBlockZeroPrice,
  reconcileCurrencyOnSave,
  normalizeRevealFlags,
  persistedFields,
  getPriceTypeLabel,
  getPriceTypeName,
  getPriceHeadline,
  getDraftBillingConfig,
} from './priceHelpers';
import { formatPrice, formatPriceInput } from '@dashboard/services/formatting';
import { isEqual } from 'lodash';
import { confirm } from '@dashboard/lib/confirm';
import { TOOLTIP_DELAY_MS } from '@dashboard/components/ui/tooltip';

// Form state holds amounts as dollar strings (e.g. "49"); formatPrice expects
// cents. Convert before formatting.
function toCents(amount, currency) {
  return formatPriceInput(amount ?? 0, currency);
}
function money(cents, currency) {
  return formatPrice({ amount: cents, currency, style: 'currency' });
}

// "Ends after N payments" for a payment plan (a recurring price with a fixed
// number of payments) — mirrors the storefront PricePicker wording so the editor
// card distinguishes a plan from an open-ended subscription. Null otherwise.
function formatEndsAfter(price) {
  const n = Number(getDraftBillingConfig(price)?.planIterations);
  if (price.billingType === 'recurring' && n > 0) {
    return `Ends after ${n} payments`;
  }
  return null;
}

// A visible-but-disabled menu item with a reason tooltip — so an action blocked
// by the current state (a preselected price can't be disabled/deleted, etc.) is
// discoverable instead of hidden. Mirrors the event ticket menu (TicketTypeItem).
function DisabledMenuItem({ children, reason }) {
  return (
    <RadixTooltip.Provider delayDuration={TOOLTIP_DELAY_MS}>
      <RadixTooltip.Root>
        <RadixTooltip.Trigger asChild>
          <DropdownMenuItem disabled onSelect={e => e.preventDefault()} className="opacity-50">
            {children}
          </DropdownMenuItem>
        </RadixTooltip.Trigger>
        <RadixTooltip.Content
          side="left"
          className="z-50 mx-2 max-w-xs select-none rounded bg-black px-1.5 py-1.5 shadow dark:bg-gray-800"
        >
          <RadixTooltip.Arrow className="fill-current text-black dark:text-gray-800 mx-auto" />
          <span className="block text-left text-sm text-white dark:text-gray-100">{reason}</span>
        </RadixTooltip.Content>
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  );
}

export default function PriceCard({
  price,
  fieldPrefix,
  index,
  isFirstPrice,
  currencyLocked = false,
  isDefault,
  onSetDefault,
  onRemoveDefault,
  onToggleEnabled,
  onDelete,
  onDuplicate,
  canDelete = false,
  deleteReason = '',
  disableReason = '',
  showPreselect = false,
  preselectReason = '',
  dragHandleProps,
  isNew = false,
}) {
  const {
    editingPriceId,
    setEditingPriceId,
    registerCloseRequest,
    requestCloseEditing,
    editingSnapshotRef,
  } = usePriceEdit();
  const { values, initialValues, setFieldValue, setValues, submitForm } = useFormikContext();
  const priceId = price._id || price.key || String(index);
  const isOpen = editingPriceId === priceId;
  // Enabled is toggled via the kebab (Disable/Enable), which autosaves immediately,
  // so it always reflects the live price — not the open-time snapshot below.
  const isDisabled = price.enabled === false;

  // Snapshot the committed price when the card opens so Cancel can revert
  // unsaved edits. Initialised to the mounted value for cards open on load.
  const [snapshot, setSnapshot] = React.useState(price);

  // Live form values for this price, compared against the open-time snapshot to
  // detect unsaved edits (so closing the card can warn before discarding).
  const priceValues = values?.prices?.[index] || {};
  // `enabled` and `order` are action-owned, never pending inline edits: the
  // kebab toggle and drag-reorder auto-save them immediately, so the open-time
  // snapshot's copies go stale the moment either action runs while this card is
  // open. Treat the live values as the snapshot's own: the actions aren't seen
  // as unsaved changes, and discarding other edits won't revert them.
  const revertSnapshot = { ...snapshot, enabled: priceValues.enabled, order: priceValues.order };
  const isDirty = !isEqual(persistedFields(priceValues), persistedFields(revertSnapshot));

  // While the card is open, the accordion row shows the committed-at-open snapshot,
  // not the live form values — editing a price must not update the row until Save
  // (which refreshes the snapshot). Collapsed cards read the live committed price.
  const headerPrice = isOpen ? snapshot : price;
  // Hidden is distinct from disabled: a hidden price is removed from the checkout
  // picker but revealable via `?show_prices=`. It's a form field (Additional settings),
  // so the header reads it from the snapshot too — the badge updates on Save.
  const isHidden = headerPrice.hidden === true;
  const currency = headerPrice.currency || 'usd';
  const label = getPriceTypeLabel(headerPrice);
  const headline = getPriceHeadline(headerPrice);
  const showHeadline = headline && headline !== label;
  // A payment plan also shows "Ends after N payments" beneath the price so it's
  // not mistaken for an open-ended subscription (matches the storefront picker).
  const endsAfter = formatEndsAfter(headerPrice);
  const discountedCents = toCents(headerPrice.discountedFromPrice, currency);

  // Close (collapse) this card, warning before discarding unsaved edits. Resolves
  // false when the user keeps the card open. Shared by the accordion-header close,
  // the Cancel button, and "Add price" (via the PriceEditContext close request).
  async function requestClose() {
    if (isDirty) {
      const discard = await confirm('Discard unsaved changes?', { confirm: 'Discard' });
      if (!discard) return { ok: false }; // keep the card open
    }
    // Always revert to the open-time snapshot (keeping the autosaved `enabled`), even
    // when not persisted-dirty: UI-only disclosure flags (`__setupFeeOpen`, …) are
    // stripped from isDirty, so ticking "Setup fee" then cancelling wouldn't roll back
    // otherwise — the disclosure would stay revealed on reopen.
    setFieldValue(fieldPrefix, revertSnapshot);
    editingSnapshotRef.current = null;
    setEditingPriceId(null);
    // Return the post-close prices so "Add price" can append its draft to a
    // consistent base — a later setFieldValue('prices', …) built from a stale
    // closure would otherwise clobber this revert.
    const revertedPrices = (values.prices || []).map((p, i) => (i === index ? revertSnapshot : p));
    return { ok: true, revertedPrices };
  }

  // While open, register the close handler so "Add price" can collapse this card
  // first — an open price and a new-price draft are mutually exclusive. Ref write,
  // not state (no re-render), mirroring submitFormRef in PricesList.
  if (isOpen) registerCloseRequest(requestClose);

  async function handleOpenChange(open) {
    if (open) {
      // Switching from another open committed card: close it first, honoring its
      // unsaved-changes prompt and reverting its edits — otherwise a half-edited price
      // the user navigates away from lingers in form state and rides along on the next
      // Save (the "change is saved without clicking Save" bug). Abort the switch if the
      // user keeps the other card open. Runs in both the create and edit flows.
      if (editingPriceId != null && editingPriceId !== priceId) {
        const { ok } = await requestCloseEditing();
        if (!ok) return;
      }
      // Mutual exclusion: on the edit flow, opening a price cancels an in-progress
      // add-price draft (warning first if the draft has unsaved input).
      if (!isNew) {
        const draftId = values.__draftPriceId;
        if (draftId != null && draftId !== priceId) {
          const draftIdx = (values.prices || []).findIndex(p => (p._id || p.key) === draftId);
          const draft = draftIdx >= 0 ? values.prices[draftIdx] : null;
          if (isPriceDraftDirty(draft)) {
            const discard = await confirm('Discard unsaved changes?', { confirm: 'Discard' });
            if (!discard) return; // keep the draft; don't open this price
          }
          if (draftIdx >= 0) {
            setFieldValue(
              'prices',
              values.prices.filter((_, i) => i !== draftIdx),
            );
          }
          setFieldValue('__draftPriceId', null);
        }
      }
      setSnapshot(price);
      // Written synchronously (not state) so a product submit in this same
      // tick already sees it — see PriceEditContext.
      editingSnapshotRef.current = price;
      setEditingPriceId(priceId);
      return;
    }
    // Collapsing via the header — Save has its own handling.
    await requestClose();
  }

  // Save persists immediately (on a saved product) but does NOT collapse the card —
  // only Cancel or the accordion header closes it. Reset the revert point to the
  // just-saved values so a later Cancel doesn't roll back past the save.
  async function handleSave() {
    // A fixed 0/blank price can't be saved: in the create flow once a price is
    // added it must be > 0; in the edit flow when 2+ prices exist and the amount
    // was actually changed. Refuse the save — the open form already shows the
    // inline "Price must be more than 0" error (AmountBlockedError).
    if (
      shouldBlockZeroPrice(price, {
        prices: values.prices,
        draftId: values.__draftPriceId,
        isNew,
        initialValues,
      })
    ) {
      return;
    }
    // Reset any reveal (Discounted from / Setup fee / Start date) ticked without a
    // value — the create flow never reseeds these from the data the way an edit-flow
    // save does. Reveal flags are UI-only (stripped before submit + in persistedFields),
    // so this never affects what's persisted or the dirty check.
    const clearsDefault =
      price.hidden === true &&
      values.defaultPriceId &&
      String(priceId) === String(values.defaultPriceId);
    await setValues({
      ...values,
      prices: (values.prices || []).map(normalizeRevealFlags),
      ...(clearsDefault && { defaultPriceId: null }),
    });
    // A deferred currency change on this price applies now: on a multi-price product
    // confirm "Change currency of all prices?" before propagating it to the product +
    // every price (both flows). Abort the save if the user cancels the confirm.
    const ok = await reconcileCurrencyOnSave({ values, setFieldValue, confirm, index });
    if (!ok) return;
    setSnapshot(normalizeRevealFlags(price));
    // Refreshing the ref to the just-saved values makes the submit's snapshot
    // substitution a no-op for Save — the new values persist naturally.
    editingSnapshotRef.current = normalizeRevealFlags(price);
    if (!isNew) await submitForm();
  }

  return (
    <div>
      <Accordion
        type="single"
        collapsible
        value={isOpen ? priceId : ''}
        onValueChange={value => handleOpenChange(value === priceId)}
        className={cn('transition-colors', (isDisabled || isHidden) && !isOpen && 'opacity-60')}
      >
        {/* Each card is its own single-item Accordion, so every item is :last-child;
          override the accordion's default last:border-b-0 to keep the row divider
          and bottom border (matching the variants list). */}
        <AccordionItem value={priceId} className="border-b last:border-b">
          <div className="flex items-center gap-0 px-1">
            {dragHandleProps && (
              <div
                {...dragHandleProps}
                className="cursor-grab active:cursor-grabbing text-gray-400 dark:text-muted-foreground shrink-0 p-1"
                aria-label="Drag to reorder"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M7 2a1 1 0 000 2 1 1 0 000-2zm4 0a1 1 0 000 2 1 1 0 000-2zM7 8a1 1 0 000 2 1 1 0 000-2zm4 0a1 1 0 000 2 1 1 0 000-2zm-4 6a1 1 0 000 2 1 1 0 000-2zm4 0a1 1 0 000 2 1 1 0 000-2z" />
                </svg>
              </div>
            )}

            <div className="flex-1 min-w-0">
              {/* Shrink the trigger's trailing chevron hit-box (default size-8, here
                  size-5) and drop the row gap so the chevron sits right next to the
                  kebab — the oversized box otherwise left dead space before the ⋯. */}
              <AccordionTrigger className="py-3 gap-2 items-center hover:no-underline min-w-0 [&>span:last-child]:size-7">
                <div className="flex flex-col min-w-0 flex-1 leading-tight">
                  <span className="text-sm font-semibold truncate min-w-0">{label}</span>
                  {/* When the merchant set a custom label, name the pricing type
                      beneath it so the type stays visible at a glance. */}
                  {headerPrice.label && (
                    <span className="text-xs font-normal text-gray-500 dark:text-muted-foreground whitespace-nowrap">
                      {getPriceTypeName(headerPrice)}
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-2 shrink-0 pl-2">
                  {/* The star stays on the preselected price whenever 2+ prices
                      exist — even after a sibling is disabled (only-one-active) or
                      if the price itself is disabled. */}
                  {isDefault && showPreselect && (
                    <Tooltip label="Preselected">
                      <Badge variant="secondary" className="text-xs shrink-0 px-1.5">
                        <StarIcon className="size-3.5 fill-current" />
                      </Badge>
                    </Tooltip>
                  )}

                  {isDisabled && (
                    <Tooltip label="Disabled prices will not be shown to customers">
                      <Badge variant="secondary" className="text-xs shrink-0">
                        Disabled
                      </Badge>
                    </Tooltip>
                  )}
                  {isHidden && (
                    <Tooltip
                      label={
                        <>
                          Hide this price and show or fill it with query parameters.{' '}
                          <Link
                            to="/docs/articles/preselect-checkout-prices"
                            target="_blank"
                            variant="light"
                          >
                            Learn more
                          </Link>
                        </>
                      }
                    >
                      <Badge variant="secondary" className="text-xs shrink-0">
                        Hidden
                      </Badge>
                    </Tooltip>
                  )}
                  {(showHeadline || endsAfter) && (
                    <span className="flex flex-col items-end leading-tight">
                      {showHeadline && (
                        <>
                          <span className="text-sm font-medium whitespace-nowrap">{headline}</span>
                          {/* Beneath the price, not beside it: side by side the two
                              amounts doubled this column's width and the title — which
                              is the only thing here that can shrink — truncated. */}
                          {discountedCents > 0 && (
                            <span className="text-xs font-normal text-stone-400 dark:text-muted-foreground line-through whitespace-nowrap">
                              {money(discountedCents, currency)}
                            </span>
                          )}
                        </>
                      )}
                      {endsAfter && (
                        <span className="text-xs font-normal text-gray-500 dark:text-muted-foreground whitespace-nowrap">
                          {endsAfter}
                        </span>
                      )}
                    </span>
                  )}
                </div>
              </AccordionTrigger>
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  appearance="icon-button"
                  size="narrow"
                  type="button"
                  aria-label="Price actions"
                  className="size-7"
                >
                  <EllipsisVerticalIcon className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={onDuplicate}>Duplicate</DropdownMenuItem>
                {/* Preselect controls appear whenever 2+ prices exist, so disabling a
                    sibling never makes them vanish from the preselected price. "Remove
                    preselected" (the default) is always available; "Set as preselected"
                    is greyed with a reason when it isn't valid — a disabled price can't
                    be preselected, and there's no choice with only one active price.
                    A lone price has no picker, so nothing shows. */}
                {showPreselect &&
                  (isDefault ? (
                    <DropdownMenuItem onSelect={onRemoveDefault}>
                      Remove preselected
                    </DropdownMenuItem>
                  ) : preselectReason ? (
                    <DisabledMenuItem reason={preselectReason}>Set as preselected</DisabledMenuItem>
                  ) : (
                    <DropdownMenuItem onSelect={onSetDefault}>Set as preselected</DropdownMenuItem>
                  ))}
                {/* Disable is greyed with a reason tooltip (rather than firing a
                    toast on click) only for the last enabled price (a page needs one
                    active price). The preselected price can be disabled — the handler
                    clears the preselection. Enable always shows normally —
                    disableReason is empty for an already-disabled price. */}
                {disableReason && price.enabled ? (
                  <DisabledMenuItem reason={disableReason}>Disable</DisabledMenuItem>
                ) : (
                  <DropdownMenuItem onSelect={onToggleEnabled}>
                    {price.enabled ? 'Disable' : 'Enable'}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                {canDelete ? (
                  <DropdownMenuItem variant="destructive" onSelect={onDelete}>
                    Delete
                  </DropdownMenuItem>
                ) : (
                  <DisabledMenuItem reason={deleteReason}>Delete</DisabledMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <AccordionContent>
            <div className="px-1 pb-4">
              <PriceForm
                fieldPrefix={fieldPrefix}
                currencyLocked={currencyLocked}
                price={price}
                lockBilling={true}
                isNew={isNew}
              />
              <div className="mt-4 flex items-center justify-end gap-2">
                {/* Match the app-wide unsaved-changes affordance (variants, fields,
                    settings forms all show this) so editing a price isn't the odd
                    one out. */}
                <FormSubmit.State visible={isDirty} />
                <Button type="button" appearance="light" onClick={requestClose}>
                  Cancel
                </Button>
                {/* A disabled price can only be opened, closed, enabled, duplicated
                    or deleted — its form is inert, so Save is disabled too. */}
                <Button
                  type="button"
                  appearance="primary"
                  onClick={handleSave}
                  disabled={isDisabled}
                >
                  Save
                </Button>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
